import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { app } from '../firebase-config';
import { Link, useNavigate } from 'react-router-dom';
import './SearchUsersPage.css';

const SearchUsersPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const db = getFirestore(app);

  // Helper to safely format location strings for display
  const getFormattedLocation = (profileObj) => {
    if (!profileObj) return "Location not specified";
    const loc = profileObj.location;
    if (!loc) return profileObj.detectedLocation || "Location not specified";
    
    const parts = [
      loc.mandal || loc.city,
      loc.district,
      loc.state
    ].filter(p => p && p !== "undefined" && String(p).trim() !== "");
    
    return parts.length > 0 ? parts.join(', ') : "Location not specified";
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setResults([]);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setSearching(true);
      try {
        const usersRef = collection(db, 'users');
        const searchQuery = searchTerm.trim();
        const capitalizedQuery = searchQuery.charAt(0).toUpperCase() + searchQuery.slice(1);

        // 1. Query by Name (Starts with)
        const qName = query(
          usersRef,
          where('name', '>=', capitalizedQuery),
          where('name', '<=', capitalizedQuery + '\uf8ff'),
          limit(10)
        );

        // 2. Query by District (Starts with)
        const qDistrict = query(
          usersRef,
          where('location.district', '>=', capitalizedQuery),
          where('location.district', '<=', capitalizedQuery + '\uf8ff'),
          limit(10)
        );

        // 3. Query by State (Starts with)
        const qState = query(
          usersRef,
          where('location.state', '>=', capitalizedQuery),
          where('location.state', '<=', capitalizedQuery + '\uf8ff'),
          limit(10)
        );

        // Run queries in parallel to ensure fast response times
        const [snapName, snapDistrict, snapState] = await Promise.all([
          getDocs(qName),
          getDocs(qDistrict).catch(() => ({ docs: [] })), // Catch gracefully if indexes are compiling
          getDocs(qState).catch(() => ({ docs: [] }))
        ]);

        // Merge and de-duplicate matches on-the-fly by user document ID
        const mergedResultsMap = new Map();

        snapName.docs.forEach(doc => mergedResultsMap.set(doc.id, { id: doc.id, ...doc.data() }));
        snapDistrict.docs.forEach(doc => mergedResultsMap.set(doc.id, { id: doc.id, ...doc.data() }));
        snapState.docs.forEach(doc => mergedResultsMap.set(doc.id, { id: doc.id, ...doc.data() }));

        setResults(Array.from(mergedResultsMap.values()).slice(0, 15));
      } catch (err) {
        console.error("Search query execution failed:", err);
      } finally {
        setSearching(false);
      }
    }, 400); // Debounced 400ms

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  return (
    <div className="search-users-page">
      <div className="search-page-header">
        <button onClick={() => navigate(-1)} className="search-back-btn">← Back</button>
        <h2>Search Profiles</h2>
      </div>

      <div className="search-input-wrapper">
        <span className="search-magnifier-icon">🔍</span>
        <input
          type="text"
          placeholder="Search people by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-users-input"
          autoFocus
        />
      </div>

      <div className="search-results-section">
        {searching ? (
          <p className="search-status-text">Searching profiles...</p>
        ) : searchTerm && results.length === 0 ? (
          <p className="search-status-text">No accounts matched "{searchTerm}"</p>
        ) : (
          <div className="search-users-list">
            {results.map(user => (
              <Link key={user.id} to={`/user/${user.id}`} className="search-user-item-card">
                <div className="search-user-avatar">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <div className="search-user-info">
                  <span className="search-user-name">{user.name}</span>
                  
                  {/* Displays their registered location and follower count */}
                  <span className="search-user-followers" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', color: '#666', marginTop: '2px', fontSize: '11px' }}>
                    <span>📍 {getFormattedLocation(user)}</span>
                    <span>•</span>
                    <span>{user.followersCount || 0} followers</span>
                  </span>
                </div>
                <span className="view-profile-arrow">→</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchUsersPage;